import React from 'react';

import { Drawer, Menu } from 'antd';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';

import IntlMessages from 'util/IntlMessages';
import { useIsMobile } from 'packages/utils';
import MenuLabel from 'containers/Sidebar/components/MenuLabel';
import TitleMenu from 'containers/Sidebar/CollapsibleSidebar/TitleMenu';
import LogoutButton from 'containers/Sidebar/CollapsibleSidebar/LogoutButton';

import styles from './styles.module.less';
import { MENU_PATHS, PATHS } from '../../constants';
import MenuIcon from 'containers/Sidebar/components/MenuIcon';
import classNames from 'classnames';
import OperondaLogo from 'components/OperondaLogo';

export default function MenuDt({ signOutDt, collapsed, setCollapsed }) {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { isMobile } = useIsMobile();
  const location = useLocation();

  const path = location?.pathname;
  const reportType = path.split('/').pop();

  const getCurrentSelectedKey = () => {
    if (!reportType) return PATHS.ATTENDANCE;

    const reportTypeMap = {
      attendance: PATHS.ATTENDANCE,
      daily: PATHS.DAILY,
      holidays: PATHS.HOLIDAYS,
      modifications: PATHS.MODIFICATIONS,
      issues: PATHS.ISSUES,
      hash: PATHS.HASH,
      active_enterprises: PATHS.ACTIVE_ENTERPRISES,
    };

    return reportTypeMap[reportType.toLowerCase()] || PATHS.ATTENDANCE;
  };

  const selectedKey = getCurrentSelectedKey();

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const menuItems = [
    {
      key: PATHS.ATTENDANCE,
      label: (
        <MenuLabel
          idLabelMessage="work.direction.attendance.report"
          to={`${url}/${MENU_PATHS.ATTENDANCE_REPORT}`}
        />
      ),
      icon: collapsed && <MenuIcon iconName="fact_check" />,
      onClick: () => collapsed && history.push(`${url}/${MENU_PATHS.ATTENDANCE_REPORT}`),
    },
    {
      key: PATHS.DAILY,
      label: (
        <MenuLabel idLabelMessage="work.direction.daily.report" to={`${url}/${MENU_PATHS.DAILY}`} />
      ),
      icon: collapsed && <MenuIcon iconName="list_alt_check" />,
      onClick: () => collapsed && history.push(`${url}/${MENU_PATHS.DAILY}`),
    },
    {
      key: PATHS.HOLIDAYS,
      label: (
        <MenuLabel
          idLabelMessage="work.direction.holidays.report"
          to={`${url}/${MENU_PATHS.HOLIDAYS}`}
        />
      ),
      icon: collapsed && <MenuIcon iconName="free_cancellation" />,
      onClick: () => collapsed && history.push(`${url}/${MENU_PATHS.HOLIDAYS}`),
    },
    {
      key: PATHS.MODIFICATIONS,
      label: (
        <MenuLabel
          idLabelMessage="work.direction.modifications.report"
          to={`${url}/${MENU_PATHS.MODIFICATIONS}`}
        />
      ),
      icon: collapsed && <MenuIcon iconName="event_repeat" />,
      onClick: () => collapsed && history.push(`${url}/${MENU_PATHS.MODIFICATIONS}`),
    },
    {
      key: PATHS.ACTIVE_ENTERPRISES,
      label: (
        <MenuLabel
          idLabelMessage="work.direction.active.enterprises.report"
          to={`${url}/${MENU_PATHS.ACTIVE_ENTERPRISES}`}
        />
      ),
      icon: collapsed && <MenuIcon iconName="business" />,
      onClick: () => collapsed && history.push(`${url}/${MENU_PATHS.ACTIVE_ENTERPRISES}`),
    },
    {
      key: PATHS.INACTIVE_ENTERPRISES,
      label: (
        <MenuLabel
          idLabelMessage="work.direction.inactive.enterprises.report"
          to={`${url}/${MENU_PATHS.INACTIVE_ENTERPRISES}`}
        />
      ),
      icon: collapsed && <MenuIcon iconName="business" />,
      onClick: () => collapsed && history.push(`${url}/${MENU_PATHS.INACTIVE_ENTERPRISES}`),
    },
    {
      key: PATHS.ISSUES,
      label: (
        <MenuLabel
          idLabelMessage="work.direction.issues.report"
          to={`${url}/${MENU_PATHS.ISSUES}`}
        />
      ),
      icon: collapsed && <MenuIcon iconName="event_busy" />,
      onClick: () => collapsed && history.push(`${url}/${MENU_PATHS.ISSUES}`),
    },
    {
      key: PATHS.HASH,
      label: <MenuLabel idLabelMessage="work.direction.hash" to={`${url}/${MENU_PATHS.HASH}`} />,
      icon: collapsed && <MenuIcon iconName="tag" />,
      onClick: () => collapsed && history.push(`${url}/${MENU_PATHS.HASH}`),
    },
  ];

  const options = menuItems.reduce((acc, val) => {
    if (collapsed && !isMobile) {
      const copy = { ...val };
      delete copy.label;
      acc = acc.concat(copy);
    } else {
      const copy = { ...val };
      delete copy.icon;
      acc = acc.concat(copy);
    }
    return acc;
  }, []);

  return (
    <>
      <div className={styles.topMenu}>
        <div className="gx-mt-2 gx-mb-3">
          <OperondaLogo onlyLogo={collapsed} />
        </div>
        <TitleMenu
          title={<IntlMessages id="work.direction.go.back" />}
          goBack={() => history.push(MENU_PATHS.PORTAL)}
          collapsed={collapsed}
        />
      </div>
      {isMobile ? (
        <Drawer
          className={classNames(styles.drawerMenu, 'gx-drawer-sidebar')}
          closable={false}
          onClose={() => setCollapsed(false)}
          placement="left"
          open={collapsed}
          onClick={() => {
            setCollapsed(false);
          }}
        >
          <div className={styles.topMenuMobile}>
            <div className="gx-flex-row gx-justify-content-center">
              <OperondaLogo />
            </div>
            <TitleMenu
              title={<IntlMessages id="work.direction.go.back" />}
              goBack={() => history.push(MENU_PATHS.PORTAL)}
            />
            <Menu
              className={styles.menuContainerMobile}
              theme="dark"
              mode="inline"
              items={options}
              selectedKeys={[selectedKey]}
            />
            <LogoutButton onSignOut={signOutDt} />
          </div>
        </Drawer>
      ) : (
        <div className="gx-d-flex gx-flex-column" style={{ height: '100vh' }}>
          <div className={styles.menuHoverContainer}>
            <Menu
              style={{ paddingLeft: '20px' }}
              className={classNames(styles.menuContainer, {
                [styles.menuCollapsed]: collapsed,
              })}
              theme="dark"
              mode="inline"
              items={options}
              selectedKeys={[selectedKey]}
            />
          </div>
          <div
            className={`${
              collapsed ? 'gx-flex-column-reverse' : 'gx-flex-row gx-ml-4 gx-flex-row'
            } gx-align-items-center gx-justify-content-between gx-pt-3 gx-pb-3`}
            style={{ flexShrink: 0 }}
          >
            <div className="gx-flex-row gx-justify-content-center">
              <LogoutButton collapsed={collapsed} onSignOut={signOutDt} />
            </div>
            <div
              className={`gx-text-white gx-flex-1 ${
                collapsed ? 'gx-text-center gx-mt-4' : 'gx-text-right gx-mr-4'
              }`}
            >
              <span
                onClick={handleToggleCollapse}
                className={classNames(
                  'material-symbols-outlined',
                  'gx-pointer',
                  styles.collapseButton,
                )}
              >
                arrow_menu_open
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
